import { createContext, useCallback, useContext, useEffect, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useLocation } from "react-router-dom";
import { Play, Star, X } from "lucide-react";
import { Img } from "../components/Img";
import { useConfig } from "./ConfigContext";
import { FALLBACK_POSTER, backdropUrl, posterUrl, year } from "../lib/api";

const WatchModalContext = createContext({ openWatch: () => {} });

const Countdown = () => {
  const [left, setLeft] = useState(3);
  useEffect(() => {
    if (left <= 0) return;
    const t = setTimeout(() => setLeft((l) => l - 1), 1000);
    return () => clearTimeout(t);
  }, [left]);
  return (
    <div className="mb-4" data-testid="watch-modal-countdown">
      <div className="flex items-center justify-between text-xs uppercase tracking-[0.18em] text-neutral-400">
        <span>{left > 0 ? "Preparing your stream" : "Your stream is ready"}</span>
        <span className="text-[#D4AF37]">{left > 0 ? `${left}s` : "Ready"}</span>
      </div>
      <div className="mt-2 h-1 overflow-hidden rounded-full bg-white/10">
        <div className="watch-progress h-full rounded-full bg-[#D4AF37]" />
      </div>
    </div>
  );
};

const WatchModal = ({ item, open, onOpenChange }) => {
  const { affiliateUrl } = useConfig();
  if (!item) return null;
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          data-testid="watch-modal-overlay"
          onClick={() => onOpenChange(false)}
          className="watch-overlay fixed inset-0 z-[60] bg-black/75 backdrop-blur-md"
        />
        <DialogPrimitive.Content
          data-testid="watch-modal"
          aria-describedby="watch-modal-overview"
          className="watch-content fixed left-1/2 top-1/2 z-[61] max-h-[92vh] w-[calc(100%-2rem)] max-w-2xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-3xl border border-[#D4AF37]/25 bg-[#0d0d0d] shadow-[0_40px_120px_-20px_rgba(0,0,0,0.95)] focus:outline-none"
        >
          <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-3xl">
            <img src={backdropUrl(item.backdrop_path, "w780")} alt="" className="h-full w-full scale-110 object-cover opacity-40 blur-2xl" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-[#0d0d0d]/80 to-[#0d0d0d]/40" />
          </div>
          <DialogPrimitive.Close
            data-testid="watch-modal-close"
            aria-label="Close"
            className="absolute right-4 top-4 z-10 grid h-9 w-9 place-items-center rounded-full border border-white/15 bg-black/50 text-neutral-300 transition-colors duration-300 hover:border-[#D4AF37] hover:text-[#D4AF37] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
          >
            <X className="h-4 w-4" />
          </DialogPrimitive.Close>
          <div className="relative flex flex-col gap-6 p-6 sm:flex-row sm:p-8">
            <div className="mx-auto w-32 shrink-0 self-start overflow-hidden rounded-xl ring-1 ring-[#D4AF37]/30 sm:mx-0 sm:w-44">
              <Img src={posterUrl(item.poster_path)} fallback={FALLBACK_POSTER} alt={item.title} className="aspect-[2/3] w-full object-cover" data-testid="watch-modal-poster" />
            </div>
            <div className="flex min-w-0 flex-1 flex-col">
              <span data-testid="watch-modal-badge" className="w-fit rounded-full border border-[#D4AF37]/50 bg-[#D4AF37]/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#D4AF37]">
                HD • Exclusive Streams
              </span>
              <DialogPrimitive.Title data-testid="watch-modal-title" className="mt-4 pr-8 font-display text-2xl font-bold leading-tight text-white sm:text-3xl">
                {item.title}
              </DialogPrimitive.Title>
              <div className="mt-3 flex items-center gap-4 text-sm text-neutral-300">
                <span className="flex items-center gap-1.5 text-[#D4AF37]" data-testid="watch-modal-rating">
                  <Star className="h-4 w-4 fill-current" /> {Number(item.vote_average || 0).toFixed(1)}
                </span>
                <span className="h-1 w-1 rounded-full bg-neutral-500" />
                <span data-testid="watch-modal-year">{year(item.release_date)}</span>
                {item.media_type === "tv" && <span className="rounded bg-white/10 px-1.5 py-0.5 text-[10px] font-semibold tracking-widest">TV</span>}
              </div>
              <p id="watch-modal-overview" className="mt-4 line-clamp-3 text-sm leading-relaxed text-neutral-400">
                {item.overview || "Press play and enjoy the show."}
              </p>
              <div className="mt-auto pt-6">
                <Countdown />
                <a
                  href={affiliateUrl || undefined}
                  data-href={affiliateUrl || undefined}
                  target="_blank"
                  rel="noopener noreferrer sponsored"
                  data-testid="watch-modal-continue"
                  onClick={() => onOpenChange(false)}
                  className="btn-gold gold-glow flex w-full items-center justify-center gap-2.5 rounded-full px-8 py-4 text-sm font-semibold uppercase tracking-[0.16em] focus:outline-none focus:ring-2 focus:ring-[#F3D566] focus:ring-offset-2 focus:ring-offset-black"
                >
                  <Play className="h-4 w-4 fill-current" /> Continue to Watch
                </a>
              </div>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export const WatchModalProvider = ({ children }) => {
  const [item, setItem] = useState(null);
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(0);
  const { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);
  const openWatch = useCallback((it) => {
    setItem(it);
    setSession((s) => s + 1);
    setOpen(true);
  }, []);
  return (
    <WatchModalContext.Provider value={{ openWatch }}>
      {children}
      <WatchModal key={session} item={item} open={open} onOpenChange={setOpen} />
    </WatchModalContext.Provider>
  );
};

export const useWatchModal = () => useContext(WatchModalContext);
